import axios from "axios"
import { IDeployment } from "../../@types/IDeployment"
import { IProject } from "../../@types/IProject"

/**
 * Helper functions for vpn setup API
 */
export const timeoutFn = (timeout: number) =>
  new Promise((_, reject) =>
    setTimeout(() => reject(new Error("timeout")), timeout)
  )

type Env = "dev" | "test" | "prod"
const TIMEOUT = 15000
const PING_TIMEOUT = 30000

export const networkAPIV2 = {
  /**
   * Gets the status of the vpn setup
   */
  getStatus: async (input: {
    client_name: string
    token: string
    mgmtUrl: string
    vpnSetupPath: string
  }) => {
    if (typeof window === "undefined") return
    const { client_name, token, mgmtUrl } = input
    const { vpnSetupPath } = input

    const res = (await Promise.race([
      fetch(`${mgmtUrl}/${vpnSetupPath}/v1/vpns/status`, {
        method: "POST",
        body: JSON.stringify({
          client_name,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
      timeoutFn(TIMEOUT),
    ])) as Response
    //Throw error if error code
    if (res.status >= 400 && res.status < 600) {
      throw new Error("Bad response from server")
    }

    const json = await res.json()
    return json as {
      vpn_status: string
    }
  },
  /**
   * Gets whether the VPN server for a project is currently running
   */
  getServerStatus: async (input: {
    project_id: string
    token: string
    mgmtUrl: string
    vpnSetupPath: string
  }): Promise<boolean | undefined> => {
    if (typeof window === "undefined") return

    const { project_id, token, mgmtUrl, vpnSetupPath } = input

    const res = (await Promise.race([
      fetch(`${mgmtUrl}/${vpnSetupPath}/v1/vpns/server-status`, {
        method: "POST",
        body: JSON.stringify({
          project_id,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
      timeoutFn(TIMEOUT),
    ])) as Response

    // Throw error if error code
    if (res.status >= 400 && res.status < 600) {
      throw new Error("Bad response from server")
    }

    const data = (await res.json()) as {
      running: boolean
    }

    return data.running
  },
  /**
   * gets openvpn profile of the vpn server once ready
   */
  getProfile: async (input: {
    client_name: string
    token: string
    mgmtUrl: string
    vpnSetupPath: string
  }) => {
    if (typeof window === "undefined") return
    const { client_name, token, mgmtUrl } = input
    const { vpnSetupPath } = input

    const res = (await Promise.race([
      fetch(`${mgmtUrl}/${vpnSetupPath}/v1/vpns/profile`, {
        method: "POST",
        body: JSON.stringify({
          client_name,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
      timeoutFn(TIMEOUT),
    ])) as Response

    //Throw error if error code
    if (res.status >= 400 && res.status < 600) {
      throw new Error("Bad response from server")
    }

    //TODO: check this later
    const text = await res.text()
    return text as string
  },
  /**
   * Tries to call a get to the IP address specified
   */
  pingServer: async (input: { ip: string }) => {
    if (typeof window === "undefined") return
    const { ip } = input

    try {
      await axios.get(`http://${ip}/`, { timeout: TIMEOUT })

      return "success"
    } catch (error) {
      return null
    }
  },
  /**
   * ping client through the vpn provisioning server
   */
  pingClient: async (input: {
    client_name: string
    token: string
    client_ip_address: string
    mgmtUrl: string
    vpnSetupPath: string
  }) => {
    if (typeof window === "undefined") return
    const {
      client_name,
      token,
      client_ip_address,
      mgmtUrl,
    } = input
    const { vpnSetupPath } = input

    try {
      await axios.post(
        `${mgmtUrl}/${vpnSetupPath}/v1/vpns/ping`,
        {
          client_name,
          client_ip_address,
        },
        {
          timeout: TIMEOUT,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )

      return "success"
    } catch (error) {
      return null
    }
  },
  /**
   * Get the assigned VPN IP address for the client
   */
  getIpAddress: async (input: {
    client_name: string
    token: string
    ip_type: string
    mgmtUrl: string
    vpnSetupPath: string
  }) => {
    if (typeof window === "undefined") return
    const {
      client_name,
      token,
      ip_type,
      mgmtUrl,
    } = input
    const { vpnSetupPath } = input

    try {
      const res = await axios.post(
        `${mgmtUrl}/${vpnSetupPath}/v1/vpns/ipaddress`,
        {
          client_name,
          ip_type,
        },
        {
          timeout: TIMEOUT,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )

      return res.data as {
        ip: string
      }
    } catch (error) {
      return null
    }
  },
  /**
   * Get client subnet CIDR of the network setup
   */
  getSubnetCidr: async (input: {

    client_name: string
    token: string
    mgmtUrl: string
    vpnSetupPath: string
  }) => {
    if (typeof window === "undefined") return
    const {
      client_name,
      token,
      mgmtUrl,
    } = input
    const { vpnSetupPath } = input

    try {
      const res = await axios.post(
        `${mgmtUrl}/${vpnSetupPath}/v1/vpns/connection-status`,
        { client_name },
        {
          timeout: TIMEOUT,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )

      return res
    } catch (error) {
      return null
    }
  },
  /**
   * Generate a VPN server for the given client's project
   */
  generateServer: async (input: {
    client_name: string
    client_cidr: string
    no_of_cameras: string
    token: string
    apiGatewayUrl: string
    organisationId: string
  }) => {
    if (typeof window === "undefined") return
    const { client_name, token } = input
    const { client_cidr, no_of_cameras } = input
    const { organisationId, apiGatewayUrl } = input

    const url = `${apiGatewayUrl}/vpnsetupHelpers/${organisationId}/${client_name}`
    const res = (await Promise.race([
      fetch(url, {
        method: "POST",
        body: JSON.stringify({
          client_name,
          client_cidr,
          no_of_cameras,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
      timeoutFn(TIMEOUT),
    ])) as Response

    //Throw error if error code
    if (res.status >= 400 && res.status < 600) {
      throw new Error("Bad response from server")
    }

    const json = await res.json()
    return json as {
      message: string
    }
  },
  /**
   * destroy the generated vpn server
   *
   */
  destroyServer: async (input: {
    client_name: string
    token: string
    organisationId: string
    apiGatewayUrl: string
  }) => {
    if (typeof window === "undefined") return
    const { client_name, token } = input
    const { organisationId, apiGatewayUrl } = input

    const url = `${apiGatewayUrl}/vpnsetupHelpers/${organisationId}/${client_name}`
    const res = (await Promise.race([
      fetch(url, {
        method: "DELETE",
        // body: JSON.stringify({
        //   environment,
        //   client_name,
        // }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
      timeoutFn(TIMEOUT),
    ])) as Response

    //Throw error if error code
    if (res.status >= 400 && res.status < 600) {
      throw new Error("Bad response from server")
    }

    //No content
    const text = await res.text()
    return text
  },
  storeRemoteHost: async (inputs: {
    token: string
    apiGatewayUrl: string
    organisationId: string
    projectId: string
    remoteHostIp: string
    remoteHostName: string
  }) => {
    const {
      token,
      apiGatewayUrl,
      organisationId,
      projectId,
      remoteHostIp,
      remoteHostName,
    } = inputs
    const url = `${apiGatewayUrl}/vpnsetupHelpers/${organisationId}/${projectId}/remoteHost`
    const remoteHostResponse = await axios.post(
      url,
      { remoteHostIp, remoteHostName },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
    //Items that are updated
    const project = remoteHostResponse.data.project as IProject
    const deployments = remoteHostResponse.data.deployments as IDeployment[]

    return { project, deployments }
  },
  /**
   * Add an additional subnet for a client
   */
  addSubnet: async (input: {
    client_name: string
    client_cidr: string
    token: string
    mgmtUrl: string
    vpnSetupPath: string
  }) => {
    if (typeof window === "undefined") return
    const {
      client_name,
      client_cidr,
      token,
      mgmtUrl,
    } = input
    const { vpnSetupPath } = input

    try {
      const res = await axios.post(
        `${mgmtUrl}/${vpnSetupPath}/v1/vpns/subnets`,
        {
          client_name,
          client_cidr,
        },
        {
          timeout: TIMEOUT,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )

      return res.data
    } catch (error) {
      return null
    }
  },
  /**
   * Delete a subnet for a client
   */
  deleteSubnet: async (input: {
    client_name: string
    client_cidr: string
    token: string
    mgmtUrl: string
    vpnSetupPath: string
  }) => {
    if (typeof window === "undefined") return
    const {
      client_name,
      client_cidr,
      token,
      mgmtUrl,
    } = input
    const { vpnSetupPath } = input

    try {
      // const res = await axios.delete(
      const res = await fetch(
        `${mgmtUrl}/${vpnSetupPath}/v1/vpns/subnets`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            client_name,
            client_cidr,
          }),
        },
      )

      const data = await res.json()
      return data
    } catch (error) {
      return null
    }
  },
}
